/**
 * Mode dégradé (CP-12) — le coffre : le référentiel administratif, chiffré sur
 * le poste par une clé dérivée du mot de passe de l'agent.
 *
 * Pourquoi chiffrer, et pourquoi ainsi. Le Tech Lead a demandé que l'accueil
 * puisse se rouvrir hors ligne **après extinction du poste** : il faut donc
 * reconnaître l'agent sans le serveur. Plutôt que de garder une empreinte de
 * son mot de passe sur un poste partagé, on chiffre le cache avec une clé
 * dérivée de ce mot de passe (PBKDF2 → AES-GCM) : le bon mot de passe
 * déchiffre, un mauvais ne donne rien — AES-GCM authentifie, il n'y a rien
 * d'autre à comparer. Et vingt mille noms ne dorment pas en clair sur le
 * disque d'un guichet.
 *
 * WebCrypto uniquement : aucune bibliothèque, aucune clé qui sort du poste.
 */

/** OWASP 2023 pour PBKDF2-HMAC-SHA256. Gardé dans le coffre : il pourra monter sans casser l'ancien. */
export const ITERATIONS_PBKDF2 = 310_000

/** Des octets adossés à un vrai `ArrayBuffer` : c'est ce que WebCrypto accepte. */
export type Octets = Uint8Array<ArrayBuffer>

export interface Coffre {
  readonly version: 1
  readonly iterations: number
  readonly sel: Octets
  readonly iv: Octets
  readonly chiffre: ArrayBuffer
}

export function nouveauSel(): Octets {
  return crypto.getRandomValues(new Uint8Array(16))
}

/** La clé du coffre. Non extractible : elle sert, elle ne se lit pas. */
export async function deriverCle(
  motDePasse: string,
  sel: Octets,
  iterations = ITERATIONS_PBKDF2,
): Promise<CryptoKey> {
  const materiau = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(motDePasse),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: sel, iterations },
    materiau,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function passer(
  octets: ArrayBuffer,
  flux: CompressionStream | DecompressionStream,
): Promise<ArrayBuffer> {
  return new Response(new Blob([octets]).stream().pipeThrough(flux)).arrayBuffer()
}

/** Compresse puis chiffre : le référentiel pèse quelques Mo en clair, dix fois moins ainsi. */
export async function sceller(
  cle: CryptoKey,
  sel: Octets,
  donnees: unknown,
  iterations = ITERATIONS_PBKDF2,
): Promise<Coffre> {
  const clair = new TextEncoder().encode(JSON.stringify(donnees))
  const compresse = await passer(clair.buffer, new CompressionStream('gzip'))
  // Un IV neuf à chaque scellement : jamais deux messages sous le même couple clé/IV.
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const chiffre = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cle, compresse)
  return { version: 1, iterations, sel, iv, chiffre }
}

/** Le contenu du coffre, ou `null` si la clé n'est pas la bonne. Ne lève pas sur un mauvais mot de passe. */
export async function ouvrirAvecCle(cle: CryptoKey, coffre: Coffre): Promise<unknown> {
  let compresse: ArrayBuffer
  try {
    compresse = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: coffre.iv }, cle, coffre.chiffre)
  } catch {
    return null
  }
  const clair = await passer(compresse, new DecompressionStream('gzip'))
  return JSON.parse(new TextDecoder().decode(clair)) as unknown
}

export async function ouvrir(motDePasse: string, coffre: Coffre): Promise<unknown> {
  return ouvrirAvecCle(await deriverCle(motDePasse, coffre.sel, coffre.iterations), coffre)
}
